import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";

// Import types for jsbarcode
import type { Options } from "jsbarcode";

export enum BarcodeFormat {
  CODE128 = "CODE128",
  CODE39 = "CODE39", 
  EAN13 = "EAN13",
  EAN8 = "EAN8",
  UPC = "UPC",
  ITF = "ITF",
  MSI = "MSI",
  pharmacode = "pharmacode",
  codabar = "codabar"
}

export enum BarcodeExportFormat {
  png,
  jpg,
  webp,
  svg
}

export interface BarcodeGeneratorState {
  inputData: string;
  format: BarcodeFormat;
  exportFormat: BarcodeExportFormat;
  width: number;
  height: number;
  fontSize: number;
  textColor: string;
  backgroundColor: string;
  lineColor: string;
  showText: boolean;
  textAlign: "left" | "center" | "right";
  textPosition: "top" | "bottom";
  margin: number;
  marginTop: number;
  marginBottom: number;
  marginLeft: number;
  marginRight: number;
}

const initialState: BarcodeGeneratorState = {
  inputData: "123456789012",
  format: BarcodeFormat.CODE128,
  exportFormat: BarcodeExportFormat.png,
  width: 2,
  height: 100,
  fontSize: 20,
  textColor: "#000000",
  backgroundColor: "#ffffff",
  lineColor: "#000000",
  showText: true,
  textAlign: "center",
  textPosition: "bottom",
  margin: 10,
  marginTop: 10,
  marginBottom: 10,
  marginLeft: 10,
  marginRight: 10,
};

export const BarcodeGeneratorSlice = createSlice({
  name: "barcodeGenerator",
  initialState,
  reducers: {
    inputDataChanged: (state, action: PayloadAction<string>) => {
      state.inputData = action.payload;
    },
    formatChanged: (state, action: PayloadAction<BarcodeFormat>) => {
      state.format = action.payload;
    },
    exportFormatChanged: (state, action: PayloadAction<BarcodeExportFormat>) => {
      state.exportFormat = action.payload;
    },
    widthChanged: (state, action: PayloadAction<number>) => {
      state.width = action.payload;
    },
    heightChanged: (state, action: PayloadAction<number>) => {
      state.height = action.payload;
    },
    fontSizeChanged: (state, action: PayloadAction<number>) => {
      state.fontSize = action.payload;
    },
    textColorChanged: (state, action: PayloadAction<string>) => {
      state.textColor = action.payload;
    },
    backgroundColorChanged: (state, action: PayloadAction<string>) => {
      state.backgroundColor = action.payload;
    },
    lineColorChanged: (state, action: PayloadAction<string>) => {
      state.lineColor = action.payload;
    },
    displayValueChanged: (state, action: PayloadAction<boolean>) => {
      state.showText = action.payload;
    },
    textAlignChanged: (state, action: PayloadAction<"left" | "center" | "right">) => {
      state.textAlign = action.payload;
    },
    textPositionChanged: (state, action: PayloadAction<"top" | "bottom">) => {
      state.textPosition = action.payload;
    },
    marginChanged: (state, action: PayloadAction<number>) => {
      state.margin = action.payload;
    },
    marginTopChanged: (state, action: PayloadAction<number>) => {
      state.marginTop = action.payload;
    },
    marginBottomChanged: (state, action: PayloadAction<number>) => {
      state.marginBottom = action.payload;
    },
    marginLeftChanged: (state, action: PayloadAction<number>) => {
      state.marginLeft = action.payload;
    },
    marginRightChanged: (state, action: PayloadAction<number>) => {
      state.marginRight = action.payload;
    },
  },
});

export const generateBarcodeAsync = createAsyncThunk<
  void,
  void,
  { state: RootState }
>("barcodeGenerator/generateBarcodeAsync", async (_, { getState }) => {
  const state = getState();
  const barcodeState = state.barcodeGenerator;

  // Lazy load jsbarcode library
  const JsBarcode = (await import("jsbarcode")).default;

  // Create a canvas element
  const canvas = document.createElement("canvas");
  
  // Configure barcode options
  const options: Options = {
    format: barcodeState.format,
    width: barcodeState.width,
    height: barcodeState.height,
    fontSize: barcodeState.fontSize,
    background: barcodeState.backgroundColor,
    lineColor: barcodeState.lineColor,
    displayValue: barcodeState.showText,
    textAlign: barcodeState.textAlign,
    textPosition: barcodeState.textPosition,
    margin: barcodeState.margin,
    marginTop: barcodeState.marginTop,
    marginBottom: barcodeState.marginBottom,
    marginLeft: barcodeState.marginLeft,
    marginRight: barcodeState.marginRight,
  };

  try {
    // Generate barcode
    JsBarcode(canvas, barcodeState.inputData, options);

    let dataUrl: string;
    
    if (barcodeState.exportFormat === BarcodeExportFormat.svg) {
      // For SVG, we need to use an SVG element instead
      const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      JsBarcode(svg, barcodeState.inputData, options);
      
      const svgString = new XMLSerializer().serializeToString(svg);
      const blob = new Blob([svgString], { type: "image/svg+xml" });
      dataUrl = URL.createObjectURL(blob);
    } else {
      // For raster formats, use canvas
      const mimeType = 
        barcodeState.exportFormat === BarcodeExportFormat.jpg ? "image/jpeg" :
        barcodeState.exportFormat === BarcodeExportFormat.webp ? "image/webp" :
        "image/png";
      
      dataUrl = canvas.toDataURL(mimeType, 0.9);
    }

    // Create download link
    const a = document.createElement("a");
    document.body.appendChild(a);
    a.href = dataUrl;
    
    const extension = 
      barcodeState.exportFormat === BarcodeExportFormat.jpg ? "jpg" :
      barcodeState.exportFormat === BarcodeExportFormat.webp ? "webp" :
      barcodeState.exportFormat === BarcodeExportFormat.svg ? "svg" :
      "png";
    
    a.download = `barcode.${extension}`;
    a.click();

    // Clean up
    if (barcodeState.exportFormat !== BarcodeExportFormat.svg) {
      URL.revokeObjectURL(dataUrl);
    }
    a.remove();
  } catch (error) {
    console.error("Error generating barcode:", error);
    // You might want to dispatch an error action here
  }
});

export const {
  inputDataChanged,
  formatChanged,
  exportFormatChanged,
  widthChanged,
  heightChanged,
  fontSizeChanged,
  textColorChanged,
  backgroundColorChanged,
  lineColorChanged,
  displayValueChanged,
  textAlignChanged,
  textPositionChanged,
  marginChanged,
  marginTopChanged,
  marginBottomChanged,
  marginLeftChanged,
  marginRightChanged,
} = BarcodeGeneratorSlice.actions;

export const selectState = (state: RootState) => state.barcodeGenerator;

export default BarcodeGeneratorSlice.reducer;
